/**
 * Központi változó registry - itt definiáljuk az összes elérhető változót
 */

export interface VariableDefinition {
  key: string;                    // Változó kulcs (pl. CUSTOMER_NAME)
  name: string;                   // Megjelenített név
  description?: string;           // Leírás
  category: string;               // Kategória
  type: 'text' | 'number' | 'date' | 'email' | 'phone' | 'currency' | 'boolean' | 'textarea';
  source: 'customer' | 'product' | 'quotation' | 'system' | 'manual';  // Honnan jön az adat
  dataPath?: string;              // Objektum útvonal (pl. 'customer.companyName')
  formatter?: (value: any) => string;  // Egyedi formázó függvény
  defaultValue?: string;          // Alapértelmezett érték
  required?: boolean;             // Kötelező-e
}

// Ügyfél változók
export const CUSTOMER_VARIABLES: VariableDefinition[] = [
  {
    key: 'CUSTOMER_COMPANY_NAME',
    name: 'Cég megnevezése',
    description: 'Az ügyfél cégének neve vagy teljes neve',
    category: 'customer',
    type: 'text',
    source: 'customer',
    dataPath: 'companyName',
    required: true
  },
  {
    key: 'CUSTOMER_TAX_NUMBER',
    name: 'Adószám',
    description: 'Az ügyfél adószáma',
    category: 'customer',
    type: 'text',
    source: 'customer',
    dataPath: 'taxNumber'
  },
  {
    key: 'CUSTOMER_REGISTRATION_NUMBER',
    name: 'Cégjegyzékszám',
    description: 'Az ügyfél cégjegyzékszáma',
    category: 'customer',
    type: 'text',
    source: 'customer',
    dataPath: 'registrationNumber'
  },
  {
    key: 'CUSTOMER_ADDRESS',
    name: 'Cég címe',
    description: 'Az ügyfél teljes címe',
    category: 'customer',
    type: 'textarea',
    source: 'customer',
    dataPath: 'address',
    formatter: (address: any) => {
      if (!address) return '';
      const parts = [];
      if (address.postalCode) parts.push(address.postalCode);
      if (address.city) parts.push(address.city);
      if (address.street) parts.push(address.street);
      return parts.join(', ');
    }
  },
  {
    key: 'CUSTOMER_EMAIL',
    name: 'E-mail cím',
    description: 'Az ügyfél e-mail címe',
    category: 'customer',
    type: 'email',
    source: 'customer',
    dataPath: 'email'
  },
  {
    key: 'CUSTOMER_PHONE',
    name: 'Telefonszám',
    description: 'Az ügyfél telefonszáma',
    category: 'customer',
    type: 'phone',
    source: 'customer',
    dataPath: 'phone'
  },
  {
    key: 'CUSTOMER_BANK_ACCOUNT',
    name: 'Bankszámlaszám',
    description: 'Az ügyfél bankszámlaszáma',
    category: 'customer',
    type: 'text',
    source: 'customer',
    dataPath: 'bankAccount'
  },
  {
    key: 'CUSTOMER_CONTACT_NAME',
    name: 'Kapcsolattartó neve',
    description: 'A kapcsolattartó személy neve',
    category: 'customer',
    type: 'text',
    source: 'customer',
    dataPath: 'contactPerson.name'
  },
  {
    key: 'CUSTOMER_CONTACT_EMAIL',
    name: 'Kapcsolattartó e-mail',
    description: 'A kapcsolattartó e-mail címe',
    category: 'customer',
    type: 'email',
    source: 'customer',
    dataPath: 'contactPerson.email'
  },
  {
    key: 'CUSTOMER_CONTACT_PHONE',
    name: 'Kapcsolattartó telefon',
    description: 'A kapcsolattartó telefonszáma',
    category: 'customer',
    type: 'phone',
    source: 'customer',
    dataPath: 'contactPerson.phone'
  },
  {
    key: 'CUSTOMER_REPRESENTATIVE',
    name: 'Képviselő neve',
    description: 'A cég képviselőjének neve',
    category: 'customer',
    type: 'text',
    source: 'customer',
    dataPath: 'representative'
  },
  {
    key: 'CUSTOMER_CONTACT_FAX',
    name: 'Kapcsolattartó fax',
    description: 'A kapcsolattartó fax száma',
    category: 'customer',
    type: 'text',
    source: 'customer',
    dataPath: 'fax'
  },
  {
    key: 'CUSTOMER_FINANCE_CONTACT_NAME',
    name: 'Pénzügyi kapcsolattartó neve',
    description: 'A pénzügyi kapcsolattartó neve',
    category: 'customer',
    type: 'text',
    source: 'customer',
    dataPath: 'financeContact.name'
  },
  {
    key: 'CUSTOMER_FINANCE_CONTACT_EMAIL',
    name: 'Pénzügyi kapcsolattartó e-mail',
    description: 'A pénzügyi kapcsolattartó e-mail címe',
    category: 'customer',
    type: 'email',
    source: 'customer',
    dataPath: 'financeContact.email'
  },
  {
    key: 'CUSTOMER_FINANCE_CONTACT_PHONE',
    name: 'Pénzügyi kapcsolattartó telefon',
    description: 'A pénzügyi kapcsolattartó telefonszáma',
    category: 'customer',
    type: 'phone',
    source: 'customer',
    dataPath: 'financeContact.phone'
  }
];

// Termék változók
export const PRODUCT_VARIABLES: VariableDefinition[] = [
  {
    key: 'PRODUCT_NAME',
    name: 'Termék neve',
    description: 'A termék vagy csomag neve',
    category: 'product',
    type: 'text',
    source: 'product',
    dataPath: 'name',
    required: true
  },
  {
    key: 'PRODUCT_CODE',
    name: 'Termékkód',
    description: 'A termék egyedi kódja',
    category: 'product',
    type: 'text',
    source: 'product',
    dataPath: 'code'
  },
  {
    key: 'PRODUCT_DESCRIPTION',
    name: 'Termék leírás',
    description: 'A termék részletes leírása',
    category: 'product',
    type: 'textarea',
    source: 'product',
    dataPath: 'description'
  },
  {
    key: 'PRODUCT_NET_PRICE',
    name: 'Nettó ár',
    description: 'A termék nettó ára',
    category: 'product',
    type: 'currency',
    source: 'product',
    dataPath: 'netPrice',
    formatter: (value: number) => value ? new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF'
    }).format(value) : ''
  },
  {
    key: 'PRODUCT_GROSS_PRICE',
    name: 'Bruttó ár',
    description: 'A termék bruttó ára',
    category: 'product',
    type: 'currency',
    source: 'product',
    dataPath: 'grossPrice',
    formatter: (value: number) => value ? new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF'
    }).format(value) : ''
  },
  {
    key: 'PRODUCT_TAX_RATE',
    name: 'ÁFA kulcs',
    description: 'A termék ÁFA kulcsa',
    category: 'product',
    type: 'text',
    source: 'product',
    dataPath: 'taxRate'
  },
  {
    key: 'PRODUCT_CATEGORY',
    name: 'Kategória',
    description: 'A termék kategóriája',
    category: 'product',
    type: 'text',
    source: 'product',
    dataPath: 'category.fullPath'
  }
];

// Csomag specifikus változók
export const PACKAGE_VARIABLES: VariableDefinition[] = [
  {
    key: 'PACKAGE_SERVICES_LIST',
    name: 'Szolgáltatások listája',
    description: 'A csomag szolgáltatásainak felsorolása',
    category: 'package',
    type: 'textarea',
    source: 'product',
    dataPath: 'packageDetails.services',
    formatter: (services: any[]) => {
      if (!services || !Array.isArray(services)) return '';
      return services.map(service => {
        let text = service.name;
        if (service.hours && service.hourlyRate) {
          text += ` (${service.hours} óra × ${service.hourlyRate} Ft)`;
        }
        return `• ${text}`;
      }).join('\n');
    }
  },
  {
    key: 'PACKAGE_SERVICES_TABLE',
    name: 'Szolgáltatások táblázat',
    description: 'A csomag szolgáltatásai táblázat formában',
    category: 'package',
    type: 'textarea',
    source: 'product',
    dataPath: 'packageDetails.services',
    formatter: (services: any[]) => {
      if (!services || !Array.isArray(services)) return '';
      let table = 'Szolgáltatás | Órák | Óradíj | Összeg\n';
      table += '--- | --- | --- | ---\n';
      services.forEach(service => {
        const hours = service.hours || 0;
        const rate = service.hourlyRate || 0;
        const total = hours * rate;
        table += `${service.name} | ${hours} | ${rate} Ft | ${total} Ft\n`;
      });
      return table;
    }
  },
  {
    key: 'PACKAGE_TOTAL_HOURS',
    name: 'Összes munkaóra',
    description: 'A csomag összes munkaórája',
    category: 'package',
    type: 'number',
    source: 'product',
    dataPath: 'packageDetails.totalHours'
  },
  {
    key: 'PACKAGE_ESTIMATED_DURATION',
    name: 'Becsült időtartam',
    description: 'A projekt becsült időtartama',
    category: 'package',
    type: 'text',
    source: 'product',
    dataPath: 'packageDetails.estimatedDuration'
  },
  {
    key: 'PACKAGE_METHODOLOGY',
    name: 'Munkamódszertan',
    description: 'A projekt munkamódszertana',
    category: 'package',
    type: 'textarea',
    source: 'product',
    dataPath: 'packageDetails.methodology'
  },
  {
    key: 'PACKAGE_DELIVERABLES',
    name: 'Szállítandók',
    description: 'A projekt szállítandói',
    category: 'package',
    type: 'textarea',
    source: 'product',
    dataPath: 'packageDetails.deliverables',
    formatter: (deliverables: string[]) => {
      if (!deliverables || !Array.isArray(deliverables)) return '';
      return deliverables.map(item => `• ${item}`).join('\n');
    }
  },
  {
    key: 'PACKAGE_REQUIREMENTS',
    name: 'Követelmények',
    description: 'A projekt követelményei',
    category: 'package',
    type: 'textarea',
    source: 'product',
    dataPath: 'packageDetails.requirements',
    formatter: (requirements: string[]) => {
      if (!requirements || !Array.isArray(requirements)) return '';
      return requirements.map(item => `• ${item}`).join('\n');
    }
  },
  {
    key: 'PACKAGE_TIMELINE',
    name: 'Ütemterv',
    description: 'A projekt ütemterve',
    category: 'package',
    type: 'textarea',
    source: 'product',
    dataPath: 'packageDetails.timeline',
    formatter: (timeline: any[]) => {
      if (!timeline || !Array.isArray(timeline)) return '';
      return timeline.map((phase, index) => 
        `${index + 1}. ${phase.phase} (${phase.duration})\n   ${phase.description}`
      ).join('\n\n');
    }
  }
];

// Rendszer változók
export const SYSTEM_VARIABLES: VariableDefinition[] = [
  {
    key: 'CURRENT_DATE',
    name: 'Mai dátum',
    description: 'A jelenlegi dátum',
    category: 'system',
    type: 'date',
    source: 'system',
    formatter: () => new Date().toLocaleDateString('hu-HU'),
    defaultValue: new Date().toLocaleDateString('hu-HU')
  },
  {
    key: 'CURRENT_YEAR',
    name: 'Jelenlegi év',
    description: 'A jelenlegi év',
    category: 'system',
    type: 'number',
    source: 'system',
    formatter: () => new Date().getFullYear().toString(),
    defaultValue: new Date().getFullYear().toString()
  },
  {
    key: 'DOCUMENT_NUMBER',
    name: 'Dokumentum száma',
    description: 'A generált dokumentum sorszáma',
    category: 'system',
    type: 'text',
    source: 'system',
    formatter: () => `DOC-${Date.now()}`,
    defaultValue: `DOC-${Date.now()}`
  },
  {
    key: 'DOCUMENT_DATE',
    name: 'Dokumentum dátuma',
    description: 'A dokumentum kiállításának dátuma',
    category: 'system',
    type: 'date',
    source: 'system',
    formatter: () => new Date().toLocaleDateString('hu-HU'),
    defaultValue: new Date().toLocaleDateString('hu-HU')
  },
  {
    key: 'CONTRACT_NUMBER',
    name: 'Szerződés száma',
    description: 'A szerződés egyedi azonosítója',
    category: 'system',
    type: 'text',
    source: 'system',
    formatter: () => `SZERZ-${Date.now()}`,
    defaultValue: `SZERZ-${Date.now()}`
  },
  {
    key: 'CONTRACT_START_DATE',
    name: 'Szerződés kezdete',
    description: 'A szerződés hatálybalépésének dátuma',
    category: 'system',
    type: 'date',
    source: 'system',
    formatter: () => new Date().toISOString().split('T')[0],
    defaultValue: new Date().toISOString().split('T')[0]
  },
  {
    key: 'CONTRACT_END_DATE',
    name: 'Szerződés vége',
    description: 'A szerződés lejáratának dátuma',
    category: 'system',
    type: 'date',
    source: 'system',
    formatter: () => new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    defaultValue: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  },
  {
    key: 'POD',
    name: 'POD',
    description: 'Point of Delivery azonosító',
    category: 'system',
    type: 'text',
    source: 'system',
    defaultValue: ''
  },
  {
    key: 'PAYMENT_DEADLINE',
    name: 'Fizetési határidő',
    description: 'A fizetés határideje',
    category: 'system',
    type: 'text',
    source: 'system',
    defaultValue: '8 nap'
  },
  {
    key: 'CONTRACT_PRICE',
    name: 'Szerződéses ár',
    description: 'A szerződés összege',
    category: 'system',
    type: 'currency',
    source: 'system',
    defaultValue: ''
  },
  {
    key: 'CURRENCY',
    name: 'Valutanem',
    description: 'A szerződés pénzneme',
    category: 'system',
    type: 'text',
    source: 'system',
    defaultValue: 'HUF'
  }
];

// Árajánlat változók
export const QUOTATION_VARIABLES: VariableDefinition[] = [
  {
    key: 'QUOTATION_NUMBER',
    name: 'Árajánlat száma',
    description: 'Az árajánlat egyedi száma',
    category: 'quotation',
    type: 'text',
    source: 'quotation',
    dataPath: 'quotationNumber'
  },
  {
    key: 'QUOTATION_DATE',
    name: 'Árajánlat dátuma',
    description: 'Az árajánlat létrehozásának dátuma',
    category: 'quotation',
    type: 'date',
    source: 'quotation',
    dataPath: 'createdAt',
    formatter: (date: Date) => date ? new Date(date).toLocaleDateString('hu-HU') : ''
  },
  {
    key: 'QUOTATION_VALID_UNTIL',
    name: 'Érvényesség vége',
    description: 'Az árajánlat érvényességének vége',
    category: 'quotation',
    type: 'date',
    source: 'quotation',
    dataPath: 'validUntil',
    formatter: (date: Date) => date ? new Date(date).toLocaleDateString('hu-HU') : ''
  },
  {
    key: 'QUOTATION_PRICE',
    name: 'Árajánlat összege',
    description: 'Az árajánlat teljes összege',
    category: 'quotation',
    type: 'currency',
    source: 'quotation',
    dataPath: 'price'
  }
];

// Összes változó
export const ALL_VARIABLES: VariableDefinition[] = [
  ...CUSTOMER_VARIABLES,
  ...PRODUCT_VARIABLES,
  ...PACKAGE_VARIABLES,
  ...SYSTEM_VARIABLES,
  ...QUOTATION_VARIABLES
];

// Kategóriák
export const VARIABLE_CATEGORIES = [
  { id: 'customer', name: 'Ügyfél adatok', icon: 'UserIcon', color: '#3b82f6' },
  { id: 'product', name: 'Termék adatok', icon: 'CubeIcon', color: '#10b981' },
  { id: 'package', name: 'Csomag adatok', icon: 'TagIcon', color: '#8b5cf6' },
  { id: 'quotation', name: 'Árajánlat adatok', icon: 'DocumentTextIcon', color: '#f59e0b' },
  { id: 'system', name: 'Rendszer adatok', icon: 'CogIcon', color: '#6b7280' }
];

/**
 * Változó érték kinyerése objektumból dataPath alapján
 */
export const getValueByPath = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

/**
 * Változó értékének formázása
 */
export const formatVariableValue = (variable: VariableDefinition, sourceData: any): string => {
  let value = sourceData;
  
  // Ha van dataPath, akkor kinyerjük az értéket
  if (variable.dataPath) {
    value = getValueByPath(sourceData, variable.dataPath);
  }
  
  // Ha nincs érték, visszaadjuk az alapértelmezett értéket
  if (value === undefined || value === null || value === '') {
    return variable.defaultValue || '';
  }
  
  // Ha van egyedi formázó, használjuk azt
  if (variable.formatter) {
    return variable.formatter(value);
  }
  
  // Alapértelmezett formázás típus szerint
  switch (variable.type) {
    case 'currency':
      const num = parseFloat(value);
      return isNaN(num) ? '' : new Intl.NumberFormat('hu-HU', {
        style: 'currency',
        currency: 'HUF'
      }).format(num);
      
    case 'date':
      const date = new Date(value);
      return isNaN(date.getTime()) ? '' : date.toLocaleDateString('hu-HU');
      
    case 'phone':
      // Magyar telefonszám formázás
      const cleaned = value.toString().replace(/\D/g, '');
      if (cleaned.length === 11 && cleaned.startsWith('36')) {
        return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
      }
      return value.toString();
      
    default:
      return value.toString();
  }
};

/**
 * Automatikus változó generálás forrás adatok alapján
 */
export const generateVariablesFromSources = (sources: {
  customer?: any;
  product?: any;
  quotation?: any;
}): Record<string, string> => {
  const variables: Record<string, string> = {};
  
  // Minden változó definíció végigiterálása
  ALL_VARIABLES.forEach(variableDef => {
    let sourceData = null;
    
    // Megfelelő forrás kiválasztása
    switch (variableDef.source) {
      case 'customer':
        sourceData = sources.customer;
        break;
      case 'product':
        sourceData = sources.product;
        break;
      case 'quotation':
        sourceData = sources.quotation;
        break;
      case 'system':
        sourceData = {}; // Rendszer változók nem igényelnek forrás adatot
        break;
    }
    
    // Változó érték generálása
    if (sourceData !== null || variableDef.source === 'system') {
      variables[variableDef.key] = formatVariableValue(variableDef, sourceData);
    }
  });
  
  return variables;
};

/**
 * Változó definíció keresése kulcs alapján
 */
export const getVariableDefinition = (key: string): VariableDefinition | undefined => {
  return ALL_VARIABLES.find(variable => variable.key === key);
};

/**
 * Változók kategória szerint csoportosítva
 */
export const getVariablesByCategory = (): Record<string, VariableDefinition[]> => {
  const grouped: Record<string, VariableDefinition[]> = {};
  
  VARIABLE_CATEGORIES.forEach(category => {
    grouped[category.id] = ALL_VARIABLES.filter(variable => variable.category === category.id);
  });
  
  return grouped;
};
